import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';

import { take } from 'rxjs';

import { isElementInViewport, LocalStorageService } from '@recruitee/common';
import { SubscriptionService } from '@recruitee/company';
import { TranslatePipe } from '@recruitee/i18n';
import {
  ArrowPosition,
  BadgeComponent,
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '@recruitee/user-interface';

import { FeatureTooltipData } from '../../product-updates.types';
import { FeatureTooltipComponent } from '../../ui/feature-tooltip/feature-tooltip.component';

@Component({
  selector: 'rt-feature-tooltip-badge',
  templateUrl: './feature-tooltip-badge.component.html',
  styleUrls: ['./feature-tooltip-badge.component.less'],
  standalone: true,
  imports: [
    BadgeComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    FeatureTooltipComponent,
    TranslatePipe,
  ],
})
export class FeatureTooltipBadgeComponent {
  @Input() public tooltipPositions: ConnectionPositionPair[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
  ];
  @Input() public badgeText: string;
  @Input() public arrowPosition: ArrowPosition = 'left';
  @Input() public tooltipConfig: FeatureTooltipData;

  @ViewChild(BadgeComponent, { read: ElementRef }) public badge: ElementRef;
  @ViewChild(ConnectedOverlayDirective) public overlay: ConnectedOverlayDirective;

  public tooltipVisited: boolean = false;
  public isTrial: boolean = false;

  constructor(
    private localStorage: LocalStorageService,
    private cdRef: ChangeDetectorRef,
    public subscription: SubscriptionService,
  ) {}

  public ngOnInit(): void {
    this.tooltipVisited = this.getTooltipCache();
    this.isTrial = this.subscription.isTrial();
  }

  public triggerTooltip($event?: Event): void {
    $event?.preventDefault();
    $event?.stopPropagation();
    this.overlay.open = true;
    this.overlay.attach.pipe(take(1)).subscribe(() => {
      const { overlayElement } = this.overlay.overlayRef;
      const isOverlayElementInViewport = isElementInViewport(overlayElement);
      if (!isOverlayElementInViewport) {
        const el = this.badge.nativeElement;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      this.setTooltipCache();
    });

    this.cdRef.detectChanges();
  }

  private setTooltipCache(): void {
    if (this.tooltipVisited) return;
    this.localStorage.set(this.localStorageKey, true);
    this.tooltipVisited = true;
    this.cdRef.detectChanges();
  }

  private getTooltipCache(): boolean {
    return !!this.localStorage.get(this.localStorageKey);
  }

  private get localStorageKey(): string {
    const { id } = this.tooltipConfig;
    return `FeatureTooltipBadge#${id}_visited`;
  }
}
